/**
 * vhtml (vhtml-ui) 语言桥接
 *
 * 企点业务项目使用 vhtml 的真实方式是 CDN + webpack externals：
 *   externals: { 'vhtml-ui': 'vhtml' }
 * 即业务运行时实际使用的是 window.vhtml，而不是 node_modules 内部副本。
 *
 * 因此本桥接只操作 CDN runtime：window.vhtml。
 * 不 import vhtml-ui/lib/locale/*，避免在 vea 这类无 vhtml-ui npm 依赖的项目中构建失败，
 * 也避免改到与真实渲染无关的 npm 副本。
 *
 * 前置条件：
 *   - 页面已加载 vhtml-ui >= 2.7.4 的 vhtml.js（企点标准 CDN）：
 *       测试环境（OA）：https://oaimg.b.qq.com/vhtml/v2/latest/vhtml.js
 *       线上：           https://bqq.gtimg.com/vhtml/v2/latest/vhtml.js
 *     URL 路径里的 `latest` 是 CDN 固定段，由 vhtml 团队在 CDN 侧指向当前发布版本，
 *     业务侧无需锁版本。
 *   - 英文环境的语言包已不再内置在 vhtml.js，而是独立 CDN 文件：
 *       测试环境（OA）：https://oaimg.b.qq.com/vhtml/v2/latest/locale/en.js
 *       线上：           https://bqq.gtimg.com/vhtml/v2/latest/locale/en.js
 *     本桥接在 applyVhtmlLocale('en-US') 时会按需动态加载该文件，无需业务在
 *     HTML 模板里手动 <script>。如已手动预加载也兼容（检测到 window.vhtml.lang.en
 *     就跳过注入）。
 *
 * 设计要点：
 *   - 环境判定不依赖业务域名枚举，而是与 vhtml.js 同源：vhtml.js 从哪里加载，
 *     locale/en.js 就从同源加载（测试 OA / 线上 gtimg 自动跟随 HTML 模板配置）。
 *   - 找不到 vhtml.js 节点时回退默认 OA URL，并 warning 提示。
 *   - 提供 setVhtmlLocaleUrls({ test, prod }) 逃生口，特殊项目可在 applyLibLocale()
 *     之前覆盖（不强制使用，留给少数边缘场景）。
 */

const DEFAULT_LOCALE_URLS = {
    // 测试环境（OA）
    test: 'https://oaimg.b.qq.com/vhtml/v2/latest/locale/en.js',
    // 线上
    prod: 'https://bqq.gtimg.com/vhtml/v2/latest/locale/en.js',
};

let overrideLocaleUrls = null;
let pendingEnLoad = null;

let warnedMissingRuntime = false;
let warnedMissingEn = false;
let warnedFallbackOrigin = false;
let warnedLoadFailed = false;

/**
 * 覆盖 vhtml 语言包 URL（特殊项目逃生口，业务侧通常不需要）。
 * 必须在 applyLibLocale() 之前调用，且只在确实无法走"与 vhtml.js 同源"的场景使用。
 *
 * @param {{ test?: string, prod?: string } | null} urls
 */
export function setVhtmlLocaleUrls(urls) {
    overrideLocaleUrls = urls && typeof urls === 'object' ? urls : null;
}

/**
 * 获取 vhtml runtime。
 * @returns {any | null}
 */
function getVhtmlRuntime() {
    if (typeof window === 'undefined') return null;
    return window.vhtml || null;
}

/**
 * 从 vhtml runtime 上读取语言包。
 *
 * vhtml 语言包由 vhtml 自身构建脚本挂到 window.vhtml.lang 下：
 *   - en.js → window.vhtml.lang.en
 *   - 中文通常内置在 vhtml 核心包里；若页面额外加载 zh 包，也尽量兼容常见命名。
 *
 * @param {any} runtime
 * @param {string} qidianLocale
 * @returns {object | null}
 */
function getVhtmlLangPackage(runtime, qidianLocale) {
    const lang = runtime && runtime.lang;
    if (!lang) return null;

    if (qidianLocale === 'en-US') {
        return lang.en || lang.enUS || lang['en-US'] || null;
    }

    return lang.zhCN || lang.zh || lang['zh-CN'] || null;
}

/**
 * 在 document.scripts 中查找已加载的 vhtml.js src，提取其同源前缀。
 * 例：https://oaimg.b.qq.com/vhtml/v2/latest/vhtml.js
 *  -> https://oaimg.b.qq.com/vhtml/v2/latest/
 *
 * @returns {string | null}
 */
function detectVhtmlScriptOrigin() {
    if (typeof document === 'undefined' || !document.scripts) return null;

    for (let i = 0; i < document.scripts.length; i++) {
        const src = document.scripts[i] && document.scripts[i].src;
        if (!src) continue;
        // 匹配 .../vhtml/v2/<seg>/vhtml.js（兼容 latest 以及其他可能的固定段）
        const match = src.match(/^(.*\/vhtml\/v2\/[^/]+\/)vhtml\.js(?:\?.*)?$/);
        if (match) return match[1];
    }

    return null;
}

/**
 * 决定 locale/en.js 的 URL。
 * 优先级：业务覆盖 > 与 vhtml.js 同源 > 默认 OA URL（兜底 + warning）。
 *
 * @returns {string}
 */
function resolveEnLocaleUrl() {
    const override = overrideLocaleUrls;
    if (override && (override.test || override.prod)) {
        // 业务自己覆盖时，按 vhtml.js 是否在 gtimg 区分 prod / test；都没探测到则取 prod 优先
        const origin = detectVhtmlScriptOrigin() || '';
        if (origin.indexOf('gtimg.com') >= 0 && override.prod) return override.prod;
        if (override.test) return override.test;
        if (override.prod) return override.prod;
    }

    const origin = detectVhtmlScriptOrigin();
    if (origin) {
        return origin + 'locale/en.js';
    }

    if (!warnedFallbackOrigin && typeof console !== 'undefined') {
        warnedFallbackOrigin = true;
        console.warn(
            '[i18n] vhtml.js script not detected on page; fallback to OA test locale URL. ' +
                'Make sure HTML template loads vhtml.js from the standard CDN, ' +
                'or call setVhtmlLocaleUrls() before applyLibLocale().'
        );
    }
    return DEFAULT_LOCALE_URLS.test;
}

/**
 * 动态注入 <script> 加载 vhtml 英文语言包。
 * 多次调用复用同一个 Promise，避免重复注入。
 *
 * @returns {Promise<void>}
 */
function loadEnLocaleScript() {
    if (pendingEnLoad) return pendingEnLoad;

    if (typeof document === 'undefined') {
        pendingEnLoad = Promise.resolve();
        return pendingEnLoad;
    }

    const url = resolveEnLocaleUrl();

    pendingEnLoad = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = false; // 与 vhtml.js 加载顺序保持一致
        script.onload = () => resolve();
        script.onerror = () => {
            if (!warnedLoadFailed && typeof console !== 'undefined') {
                warnedLoadFailed = true;
                console.warn('[i18n] failed to load vhtml English locale: ' + url);
            }
            // 不 reject：保持降级语义，让上层调用方继续执行
            resolve();
        };
        (document.head || document.documentElement).appendChild(script);
    });

    return pendingEnLoad;
}

/**
 * 在拿到语言包之后真正调用 runtime.locale(pkg)。
 * 若仍未拿到（CDN 失败 / 网络异常），给一次性 warning。
 */
function applyEnFromRuntime() {
    const runtime = getVhtmlRuntime();
    if (!runtime || typeof runtime.locale !== 'function') return;
    const pkg = getVhtmlLangPackage(runtime, 'en-US');
    if (pkg) {
        runtime.locale(pkg);
    } else if (!warnedMissingEn && typeof console !== 'undefined') {
        warnedMissingEn = true;
        console.warn('[i18n] vhtml English locale package not found: window.vhtml.lang.en.');
    }
}

/**
 * 将指定 qidianLocale 应用到 vhtml。
 *
 * 注意：vhtml 暴露的是 locale(langPackage)，不是 setLocale(localeName)。
 * 本桥接把语言名 → runtime 语言包的映射封装起来，业务代码只传 qidianLocale。
 *
 * 英文语言包采用按需 CDN 加载策略；返回 Promise，调用方可选择 await，
 * 也可 fire-and-forget（默认 onLibLocaleChange 走整页刷新策略，无需 await）。
 *
 * @param {string} qidianLocale
 * @returns {Promise<void>}
 */
export function applyVhtmlLocale(qidianLocale) {
    const runtime = getVhtmlRuntime();
    if (!runtime || typeof runtime.locale !== 'function') {
        if (!warnedMissingRuntime && typeof console !== 'undefined') {
            warnedMissingRuntime = true;
            console.warn('[i18n] window.vhtml.locale is not available; skip vhtml locale bridge.');
        }
        return Promise.resolve();
    }

    const normalized = qidianLocale === 'en-US' ? 'en-US' : 'zh-CN';

    if (normalized === 'en-US') {
        // 已就绪（业务已在 HTML 模板里手动预加载）→ 同步分支
        if (getVhtmlLangPackage(runtime, 'en-US')) {
            applyEnFromRuntime();
            return Promise.resolve();
        }
        // 未就绪 → 按需 CDN 加载
        return loadEnLocaleScript().then(() => {
            applyEnFromRuntime();
        });
    }

    // 中文是 vhtml 核心包默认语言。若页面额外暴露了 zh 包则显式切回；否则不处理即可。
    const zhPkg = getVhtmlLangPackage(runtime, 'zh-CN');
    if (zhPkg) {
        runtime.locale(zhPkg);
    }
    return Promise.resolve();
}
